import { useState, useEffect } from 'react';
import { Grid, TextField, Typography, Divider, FormControlLabel, Switch, Box, MenuItem } from '@mui/material';
import ErpDateField from '../ErpDateField';
import ErpTalanaSelect, { getTalanaCatalog } from '../ErpTalanaSelect';
import { InfoTip } from '../LabelWithInfo';
import { parseAmount, amountHint } from '../parseAmount';
import { useTranslation } from 'react-i18next';
import useStore from '../../config/store';
import { isTalanaRef, contractKindFromName, healthSystemFromItem, toTalanaRef, normalizeName } from '../talanaRefs';
import {
  isValidRut, formatRutInput, canonicalRut, isValidEmail, isValidPhone, formatPhoneInput,
  collectFormatErrors, normalizeForSave,
} from '../fieldValidators';

/** Campos con validación de formato (2026-09-22): RUT con DV, correo, teléfono */
export const EMPLOYEE_FORMAT_FIELDS = {
  'personal.rut': 'rut',
  'personal.email': 'email',
  'personal.phone': 'phone',
};
export const employeeFormatErrors = (form) => collectFormatErrors(form, EMPLOYEE_FORMAT_FIELDS);

/**
 * Campos compartidos de la ficha de colaborador (fase B): datos personales
 * ampliados + visa + previsión + datos de pago + condiciones contractuales.
 * Los usa el wizard de creación y la pestaña de datos del detalle. Los datos
 * viven en la ficha; el diálogo de validación solo revisa completitud.
 */

// AFP, salud, banco, tipo de contrato, jornada, nacionalidad, comuna, ciudad y
// sucursal se eligen de los catálogos de Talana (ErpTalanaSelect) y se guardan
// como referencia { uid, talanaId, name, code } — decisión 2026-09-22. Sexo,
// estado civil, forma de pago y tipo de cuenta siguen siendo enums propios
// (Talana no tiene catálogo para ellos; el export los mapea).
const GENDERS = ['male', 'female', 'other'];
const MARITAL_STATUSES = ['single', 'married', 'civil_union', 'divorced', 'widowed'];
const PAYMENT_METHODS = ['deposit', 'vale_vista', 'check', 'cash'];
const ACCOUNT_TYPES = ['corriente', 'vista', 'rut', 'ahorro'];

export const emptyEmployeeForm = {
  personal: {
    names: '', firstLastName: '', secondLastName: '', rut: '',
    birthDate: '', country: '', gender: '', maritalStatus: '',
    street: '', streetNumber: '', apartment: '', commune: '', city: '',
    phone: '', email: '',
  },
  visa: { required: false, type: '', expiresAt: '' },
  prevision: { pensionFund: '', healthSystem: '', healthProvider: '', healthPlanValue: '', healthPlanUnit: 'uf', isPensioner: false },
  payment: { method: '', bank: '', accountType: '', accountNumber: '' },
  employment: {
    startDate: '', endDate: '', schedule: '', shift: '', contractType: '',
    jobFunctions: '', workDays: '', weeklyHours: '', mealBreakMinutes: '', workplaceAddress: '', workplaceCommune: '',
    workplaceCity: '', branch: '',
  },
};

export const toEmployeeForm = (record) => ({
  personal: {
    names: record.personal?.names || '',
    firstLastName: record.personal?.firstLastName || '',
    secondLastName: record.personal?.secondLastName || '',
    rut: record.personal?.rut || '',
    birthDate: record.personal?.birthDate || '',
    // Fallback: fichas creadas antes del renombre nationality -> country
    country: record.personal?.country || record.personal?.nationality || '',
    gender: record.personal?.gender || '',
    maritalStatus: record.personal?.maritalStatus || '',
    // Fallback: la dirección de texto libre legada se muestra como calle
    street: record.personal?.street || record.personal?.address || '',
    streetNumber: record.personal?.streetNumber || '',
    apartment: record.personal?.apartment || '',
    commune: record.personal?.commune || '',
    city: record.personal?.city || '',
    phone: record.personal?.phone || '',
    email: record.personal?.email || '',
  },
  visa: {
    required: Boolean(record.visa?.required),
    type: record.visa?.type || '',
    expiresAt: record.visa?.expiresAt || '',
  },
  prevision: {
    pensionFund: record.prevision?.pensionFund || '',
    healthSystem: record.prevision?.healthSystem || '',
    healthProvider: record.prevision?.healthProvider || '',
    healthPlanValue: record.prevision?.healthPlanValue ?? '',
    healthPlanUnit: record.prevision?.healthPlanUnit || 'uf',
    isPensioner: Boolean(record.prevision?.isPensioner),
  },
  payment: {
    method: record.payment?.method || '',
    bank: record.payment?.bank || '',
    accountType: record.payment?.accountType || '',
    accountNumber: record.payment?.accountNumber || '',
  },
  employment: {
    startDate: record.employment?.startDate || '',
    endDate: record.employment?.endDate || '',
    schedule: record.employment?.schedule || '',
    shift: record.employment?.shift || '',
    contractType: record.employment?.contractType || '',
    jobFunctions: record.employment?.jobFunctions || '',
    workDays: record.employment?.workDays ?? '',
    weeklyHours: record.employment?.weeklyHours ?? '',
    mealBreakMinutes: record.employment?.mealBreakMinutes ?? '',
    workplaceAddress: record.employment?.workplaceAddress || '',
    workplaceCommune: record.employment?.workplaceCommune || '',
    workplaceCity: record.employment?.workplaceCity || '',
    branch: record.employment?.branch || '',
  },
});

const orNull = (v) => (v === '' || v === undefined ? null : v);
const numOrNull = (v) => (v === '' || v === null || v === undefined ? null : Number(v));

const toEmployeePayloadNormalized = (form) => ({
  personal: Object.fromEntries(
    Object.entries(form.personal).map(([k, v]) => [k, orNull(v)])
  ),
  visa: {
    required: form.visa.required,
    type: form.visa.required ? form.visa.type || null : null,
    expiresAt: form.visa.required ? form.visa.expiresAt || null : null,
  },
  prevision: {
    pensionFund: orNull(form.prevision.pensionFund),
    healthSystem: orNull(form.prevision.healthSystem),
    healthProvider: orNull(form.prevision.healthProvider), // referencia Talana (Fonasa es un ítem más)
    healthPlanValue: form.prevision.healthSystem === 'isapre' ? parseAmount(form.prevision.healthPlanValue) : null, // "3,5" UF o "85.000" pesos
    healthPlanUnit: form.prevision.healthSystem === 'isapre' ? form.prevision.healthPlanUnit || 'uf' : null,
    isPensioner: Boolean(form.prevision.isPensioner),
  },
  payment: {
    method: orNull(form.payment.method),
    bank: orNull(form.payment.bank),
    accountType: orNull(form.payment.accountType),
    accountNumber: orNull(form.payment.accountNumber),
  },
  employment: {
    startDate: orNull(form.employment.startDate),
    endDate: orNull(form.employment.endDate),
    schedule: orNull(form.employment.schedule),
    shift: orNull(form.employment.shift),
    contractType: orNull(form.employment.contractType),
    jobFunctions: orNull(form.employment.jobFunctions),
    workDays: numOrNull(form.employment.workDays),
    weeklyHours: numOrNull(form.employment.weeklyHours),
    mealBreakMinutes: numOrNull(form.employment.mealBreakMinutes),
    workplaceAddress: orNull(form.employment.workplaceAddress),
    workplaceCommune: orNull(form.employment.workplaceCommune),
    workplaceCity: orNull(form.employment.workplaceCity),
    branch: orNull(form.employment.branch),
  },
});

// RUT canónico, teléfono solo dígitos, correo en minúsculas → luego el payload
export const toEmployeePayload = (rawForm) =>
  toEmployeePayloadNormalized(normalizeForSave(rawForm, EMPLOYEE_FORMAT_FIELDS));

export const applyEmployeeFieldChange = (prev, path, value) => {
  const [section, key] = path.split('.');
  return { ...prev, [section]: { ...prev[section], [key]: value } };
};

/**
 * Requisitos para validar la ficha (mantener en sincronía con
 * REQUIRED_FOR_VALIDATION del backend, erpEmployeeRecord.controller.js).
 * Cada entrada: [ruta, clave i18n de la etiqueta, predicado de completitud].
 */
// Los campos de catálogo exigen referencia Talana (no basta el texto legado):
// así el error aparece en la validación del BP y no en el cierre de remuneraciones.
export const REQUIRED_FOR_VALIDATION = [
  ['personal.names', 'erp.employees.form.names', (f) => f.personal.names],
  ['personal.firstLastName', 'erp.employees.form.firstLastName', (f) => f.personal.firstLastName],
  ['personal.secondLastName', 'erp.employees.form.secondLastName', (f) => f.personal.secondLastName],
  ['personal.rut', 'erp.employees.form.rut', (f) => f.personal.rut],
  ['personal.birthDate', 'erp.employees.form.birthDate', (f) => f.personal.birthDate],
  ['personal.gender', 'erp.employees.form.gender', (f) => f.personal.gender],
  ['personal.street', 'erp.employees.form.street', (f) => f.personal.street],
  ['personal.streetNumber', 'erp.employees.form.streetNumber', (f) => f.personal.streetNumber],
  ['personal.country', 'erp.employees.form.country', (f) => isTalanaRef(f.personal.country)],
  ['personal.commune', 'erp.employees.form.commune', (f) => isTalanaRef(f.personal.commune)],
  ['personal.city', 'erp.employees.form.city', (f) => isTalanaRef(f.personal.city)],
  ['prevision.pensionFund', 'erp.employees.form.pensionFund', (f) => isTalanaRef(f.prevision.pensionFund)],
  ['prevision.healthProvider', 'erp.employees.form.healthProvider', (f) => isTalanaRef(f.prevision.healthProvider)],
  ['payment.method', 'erp.employees.form.paymentMethod', (f) => f.payment.method],
  ['payment.bank', 'erp.employees.form.bank', (f) => f.payment.method !== 'deposit' || isTalanaRef(f.payment.bank)],
  ['payment.accountType', 'erp.employees.form.accountType', (f) => f.payment.method !== 'deposit' || f.payment.accountType],
  ['payment.accountNumber', 'erp.employees.form.accountNumber', (f) => f.payment.method !== 'deposit' || f.payment.accountNumber],
  ['employment.contractType', 'erp.employees.employment.contractType', (f) => isTalanaRef(f.employment.contractType)],
  ['employment.startDate', 'erp.employees.employment.startDate', (f) => f.employment.startDate],
  ['employment.schedule', 'erp.employees.employment.schedule', (f) => isTalanaRef(f.employment.schedule)],
  ['employment.branch', 'erp.employees.employment.branch', (f) => isTalanaRef(f.employment.branch)],
  ['employment.workDays', 'erp.employees.employment.workDays', (f) => f.employment.workDays],
  ['employment.weeklyHours', 'erp.employees.employment.weeklyHours', (f) => f.employment.weeklyHours],
];

export const missingForValidation = (form) =>
  REQUIRED_FOR_VALIDATION.filter(([, , ok]) => !ok(form));

const ErpEmployeeFormFields = ({ form, onChange }) => {
  const { t } = useTranslation();
  const { organization, authToken } = useStore.getState();

  // --- Coherencia comuna → ciudad (2026-09-24) ---
  // En el árbol de Talana la comuna cuelga de la provincia y la ciudad de la
  // región: no hay enlace directo. Se sube por `parent` desde la comuna y se
  // aceptan las ciudades que comparten algún ancestro (la región). Si existe
  // una ciudad homónima a la comuna se autocompleta.
  const [locations, setLocations] = useState(null);
  useEffect(() => {
    if (!organization) return undefined;
    let cancelled = false;
    getTalanaCatalog({ organization, authToken, type: 'locations' })
      .then((items) => { if (!cancelled) setLocations(items); })
      .catch(() => { if (!cancelled) setLocations([]); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organization]);

  // Ancestros de un nodo (provincia, región…) sin el país, que es ancestro de todo
  const ancestorsOf = (talanaId) => {
    const byId = new Map((locations || []).map((i) => [String(i.talanaId), i]));
    const chain = new Set();
    let node = byId.get(String(talanaId));
    let guard = 0;
    while (node && node.parent !== null && node.parent !== undefined && guard < 10) {
      const parent = byId.get(String(node.parent));
      if (parent && normalizeName(parent.extra?.tipo) !== 'pais') chain.add(String(parent.talanaId));
      node = parent;
      guard += 1;
    }
    return chain;
  };
  const shareAncestor = (aId, bId) => {
    const a = ancestorsOf(aId);
    if (a.size === 0) return true; // sin árbol conocido: no filtrar
    const b = ancestorsOf(bId);
    return [...a].some((x) => b.has(x));
  };

  // Igual para la persona y para el lugar de trabajo (ciudad del lugar de trabajo: contrato en Izydocs, 2026-10-20)
  const coherentWith = (commune) => (city) => (isTalanaRef(commune) && locations ? shareAncestor(commune.talanaId, city.talanaId) : true);
  const communeChangeFor = (communePath, cityPath, currentCity) => (ref) => {
    onChange(communePath, ref);
    if (!isTalanaRef(ref) || !locations) return;
    // Ciudad homónima → autocompletar; si la ciudad actual no es coherente, limpiarla
    const wanted = normalizeName(ref.name);
    const sameName = locations.find((i) => normalizeName(i.extra?.tipo) === 'ciudad' && normalizeName(i.name) === wanted && i.active !== false);
    if (sameName) {
      onChange(cityPath, toTalanaRef(sameName));
      return;
    }
    if (isTalanaRef(currentCity) && !shareAncestor(ref.talanaId, currentCity.talanaId)) onChange(cityPath, '');
  };

  const communeRef = form.personal.commune;
  const isCityCoherent = coherentWith(communeRef);
  const handleCommuneChange = communeChangeFor('personal.commune', 'personal.city', form.personal.city);
  const workplaceCommuneRef = form.employment.workplaceCommune;
  const handleWorkplaceCommuneChange = communeChangeFor('employment.workplaceCommune', 'employment.workplaceCity', form.employment.workplaceCity);

  // Select desde un catálogo Talana sincronizado; guarda la referencia en `path`
  const talana = (path, labelKey, type, props = {}) => {
    const [section, key] = path.split('.');
    return (
      <ErpTalanaSelect type={type} label={t(labelKey)} value={form[section][key] ?? ''}
        onChange={(ref) => onChange(path, ref)} {...props} />
    );
  };

  const text = (path, labelKey, props = {}) => {
    const [section, key] = path.split('.');
    return (
      <TextField fullWidth size="small" label={t(labelKey)} value={form[section][key] ?? ''}
        onChange={(e) => onChange(path, e.target.value)} {...props} />
    );
  };

  // Campos con formato: RUT con guion en pantalla y canónico en el estado; correo; teléfono
  const formatted = (path, labelKey, kind, props = {}) => {
    const [section, key] = path.split('.');
    const value = form[section][key] ?? '';
    const check = kind === 'rut' ? isValidRut : kind === 'email' ? isValidEmail : isValidPhone;
    const invalid = !check(value);
    const display = kind === 'rut' ? formatRutInput(value) : value;
    const parse = kind === 'rut' ? canonicalRut : kind === 'phone' ? formatPhoneInput : (v) => v;
    return (
      <TextField fullWidth size="small" label={t(labelKey)} value={display}
        type={kind === 'email' ? 'email' : 'text'}
        inputProps={kind === 'rut' ? { maxLength: 10 } : kind === 'phone' ? { inputMode: 'tel', maxLength: 16 } : undefined}
        onChange={(e) => onChange(path, parse(e.target.value))}
        {...props}
        error={invalid}
        helperText={invalid ? t(`erp.validation.${kind}`) : props.helperText} />
    );
  };

  // Fecha DD/MM/AAAA en pantalla, ISO YYYY-MM-DD en el estado (CLAUDE.md → Fechas)
  const date = (path, labelKey) => {
    const [section, key] = path.split('.');
    return (
      <ErpDateField label={t(labelKey)} value={form[section][key] ?? ''}
        onChange={(value) => onChange(path, value)} />
    );
  };

  const select = (path, labelKey, options, optionLabel) => {
    const [section, key] = path.split('.');
    return (
      <TextField select fullWidth size="small" label={t(labelKey)} value={form[section][key] ?? ''}
        onChange={(e) => onChange(path, e.target.value)}>
        <MenuItem value="">{t('select')}</MenuItem>
        {options.map((option) => (
          <MenuItem key={option} value={option}>
            {optionLabel ? t(`${optionLabel}.${option}`) : option}
          </MenuItem>
        ))}
      </TextField>
    );
  };

  const section = (labelKey) => (
    <Grid item xs={12}>
      <Typography variant="subtitle1" fontWeight={700}>{t(labelKey)}</Typography>
      <Divider sx={{ mt: 0.5 }} />
    </Grid>
  );

  return (
    <Grid container spacing={2}>
      {section('erp.employees.form.sectionPersonal')}
      <Grid item xs={12} sm={4}>{text('personal.names', 'erp.employees.form.names', { required: true })}</Grid>
      <Grid item xs={12} sm={4}>{text('personal.firstLastName', 'erp.employees.form.firstLastName')}</Grid>
      <Grid item xs={12} sm={4}>{text('personal.secondLastName', 'erp.employees.form.secondLastName')}</Grid>
      <Grid item xs={12} sm={3}>
        {formatted('personal.rut', 'erp.employees.form.rut', 'rut', { helperText: t('erp.employees.form.rutHelper') })}
      </Grid>
      <Grid item xs={12} sm={3}>
        {date('personal.birthDate', 'erp.employees.form.birthDate')}
      </Grid>
      <Grid item xs={12} sm={3}>
        {talana('personal.country', 'erp.employees.form.country', 'countries', { helperText: t('erp.employees.form.countryHelper') })}
      </Grid>
      <Grid item xs={12} sm={3}>{formatted('personal.phone', 'erp.employees.form.phone', 'phone')}</Grid>
      <Grid item xs={12} sm={3}>{select('personal.gender', 'erp.employees.form.gender', GENDERS, 'erp.employees.form.genderOptions')}</Grid>
      <Grid item xs={12} sm={3}>{select('personal.maritalStatus', 'erp.employees.form.maritalStatus', MARITAL_STATUSES, 'erp.employees.form.maritalOptions')}</Grid>
      <Grid item xs={12} sm={6}>{formatted('personal.email', 'erp.employees.form.email', 'email')}</Grid>
      <Grid item xs={12} sm={4}>{text('personal.street', 'erp.employees.form.street')}</Grid>
      <Grid item xs={6} sm={2}>{text('personal.streetNumber', 'erp.employees.form.streetNumber')}</Grid>
      <Grid item xs={6} sm={2}>{text('personal.apartment', 'erp.employees.form.apartment')}</Grid>
      <Grid item xs={6} sm={2}>
        <ErpTalanaSelect type="locations" tipo="Comuna" label={t('erp.employees.form.commune')}
          value={form.personal.commune ?? ''} onChange={handleCommuneChange} />
      </Grid>
      <Grid item xs={6} sm={2}>
        {talana('personal.city', 'erp.employees.form.city', 'locations', {
          tipo: 'Ciudad',
          filter: isCityCoherent,
          helperText: isTalanaRef(communeRef) ? t('erp.employees.form.cityFilteredHint') : null,
        })}
      </Grid>

      {section('erp.employees.form.sectionVisa')}
      <Grid item xs={12} sm={4}>
        <FormControlLabel
          control={<Switch checked={form.visa.required}
            onChange={(e) => onChange('visa.required', e.target.checked)} />}
          label={(<Box component="span" sx={{ display: 'inline-flex', alignItems: 'center' }}>{t('erp.employees.form.visaRequired')}<InfoTip title={t('erp.tooltips.employee.visaRequired')} /></Box>)} />
      </Grid>
      {form.visa.required && (
        <>
          <Grid item xs={12} sm={4}>{text('visa.type', 'erp.employees.form.visaType')}</Grid>
          <Grid item xs={12} sm={4}>
            {date('visa.expiresAt', 'erp.employees.form.visaExpiresAt')}
          </Grid>
        </>
      )}

      {section('erp.employees.form.sectionPrevision')}
      <Grid item xs={12} sm={3}>
        {talana('prevision.pensionFund', 'erp.employees.form.pensionFund', 'afps', {
          // tipo 1 = AFP; tipo 2 = cajas de previsión antiguas (INP)
          filter: (item) => item.extra?.tipo === 1 || item.extra?.tipo === '1' || item.extra?.tipo === null || item.extra?.tipo === undefined,
          refExtra: (item) => ({ razonSocial: item.extra?.razonSocial || null }),
        })}
      </Grid>
      <Grid item xs={12} sm={3}>
        <ErpTalanaSelect type="healthInstitutions" label={t('erp.employees.form.healthProvider')}
          value={form.prevision.healthProvider ?? ''}
          refExtra={(item) => ({ tipo: item.extra?.tipo || null })}
          helperText={form.prevision.healthSystem ? t(`erp.employees.form.healthOptions.${form.prevision.healthSystem}`) : null}
          onChange={(ref) => {
            onChange('prevision.healthProvider', ref);
            // 'fonasa' | 'isapre' | 'none' se deriva del tipo del ítem (Fonasa = id 1)
            onChange('prevision.healthSystem', ref && typeof ref === 'object' ? healthSystemFromItem({ extra: { tipo: ref.tipo } }) : '');
          }} />
      </Grid>
      {form.prevision.healthSystem === 'isapre' && (
        <>
          <Grid item xs={6} sm={2}>
            {text('prevision.healthPlanValue', 'erp.employees.form.healthPlanValue', {
              inputProps: { inputMode: 'decimal' },
              helperText: [t('erp.employees.form.healthPlanHelper'), amountHint(form.prevision.healthPlanValue)].filter(Boolean).join(' · '),
            })}
          </Grid>
          <Grid item xs={6} sm={1}>
            <TextField select fullWidth size="small" label={t('erp.employees.form.healthPlanUnit')}
              value={form.prevision.healthPlanUnit || 'uf'}
              onChange={(e) => onChange('prevision.healthPlanUnit', e.target.value)}>
              <MenuItem value="uf">UF</MenuItem>
              <MenuItem value="clp">CLP</MenuItem>
            </TextField>
          </Grid>
        </>
      )}
      <Grid item xs={12} sm={3}>
        <FormControlLabel
          control={<Switch checked={form.prevision.isPensioner}
            onChange={(e) => onChange('prevision.isPensioner', e.target.checked)} />}
          label={t('erp.employees.form.isPensioner')} />
      </Grid>

      {section('erp.employees.form.sectionPayment')}
      <Grid item xs={12} sm={3}>{select('payment.method', 'erp.employees.form.paymentMethod', PAYMENT_METHODS, 'erp.employees.form.paymentMethods')}</Grid>
      {form.payment.method === 'deposit' && (
        <>
          <Grid item xs={12} sm={3}>{talana('payment.bank', 'erp.employees.form.bank', 'banks')}</Grid>
          <Grid item xs={12} sm={3}>{select('payment.accountType', 'erp.employees.form.accountType', ACCOUNT_TYPES, 'erp.employees.form.accountTypes')}</Grid>
          <Grid item xs={12} sm={3}>{text('payment.accountNumber', 'erp.employees.form.accountNumber', { inputProps: { autoComplete: 'off', inputMode: 'numeric' } })}</Grid>
        </>
      )}

      {section('erp.employees.form.sectionEmployment')}
      <Grid item xs={12} sm={3}>
        {talana('employment.contractType', 'erp.employees.employment.contractType', 'contractTypes', {
          // kind = tipo interno de Amanda (fixed_term | indefinite | work_or_task | null) para causal EST y export
          refExtra: (item) => ({ kind: contractKindFromName(item.name) }),
          helperText: isTalanaRef(form.employment.contractType) && form.employment.contractType.kind
            ? t(`erp.employees.employment.contractTypes.${form.employment.contractType.kind}`)
            : null,
        })}
      </Grid>
      <Grid item xs={6} sm={3}>
        {date('employment.startDate', 'erp.employees.employment.startDate')}
      </Grid>
      <Grid item xs={6} sm={3}>
        {date('employment.endDate', 'erp.employees.employment.endDate')}
      </Grid>
      <Grid item xs={12} sm={3}>
        {talana('employment.schedule', 'erp.employees.employment.schedule', 'workSchedules', { helperText: t('erp.employees.employment.scheduleHelper') })}
      </Grid>
      <Grid item xs={12} sm={6}>{text('employment.shift', 'erp.employees.employment.shift', { helperText: t('erp.employees.employment.shiftHelper') })}</Grid>
      <Grid item xs={6} sm={2}>{text('employment.workDays', 'erp.employees.employment.workDays', { type: 'number' })}</Grid>
      <Grid item xs={6} sm={2}>{text('employment.weeklyHours', 'erp.employees.employment.weeklyHours', { type: 'number' })}</Grid>
      <Grid item xs={6} sm={2}>
        {text('employment.mealBreakMinutes', 'erp.employees.employment.mealBreakMinutes', {
          type: 'number', inputProps: { min: 0, step: 5 }, helperText: t('erp.employees.employment.mealBreakMinutesHelper'),
        })}
      </Grid>
      <Grid item xs={12}>{text('employment.jobFunctions', 'erp.employees.employment.jobFunctions', { multiline: true, minRows: 2 })}</Grid>
      <Grid item xs={12} sm={4}>{text('employment.workplaceAddress', 'erp.employees.employment.workplaceAddress')}</Grid>
      <Grid item xs={6} sm={3}>
        <ErpTalanaSelect type="locations" tipo="Comuna" label={t('erp.employees.employment.workplaceCommune')}
          value={form.employment.workplaceCommune ?? ''} onChange={handleWorkplaceCommuneChange} />
      </Grid>
      <Grid item xs={6} sm={2}>
        {talana('employment.workplaceCity', 'erp.employees.employment.workplaceCity', 'locations', {
          tipo: 'Ciudad',
          filter: coherentWith(workplaceCommuneRef),
          helperText: isTalanaRef(workplaceCommuneRef) ? t('erp.employees.form.cityFilteredHint') : null,
        })}
      </Grid>
      <Grid item xs={12} sm={3}>
        {talana('employment.branch', 'erp.employees.employment.branch', 'branches', { remote: true, helperText: t('erp.employees.employment.branchHelper') })}
      </Grid>
    </Grid>
  );
};

export default ErpEmployeeFormFields;
